import { createContext, useContext, useEffect } from "react";

import { ApiError } from "@/lib/api";

/**
 * Subset minimo di UseMutationResult richiesto da useResetMfaErrorOnVerify.
 * Evitiamo di tipizzare l'intero `UseMutationResult<TData, TError, TVars>`
 * cosi' il consumer non deve fare cast del proprio mutation con generics
 * specifici (TanStack Query non e' covariante sui generic).
 */
interface ResettableMutation {
  error: unknown;
  reset: () => void;
}

/**
 * Imperative MFA verify prompt context (Phase 10F strict MFA gate).
 *
 * Owner: `<MfaVerifyProvider>` in components/auth/MfaVerifyDialog.tsx.
 * Consumer: any callsite that vuole triggerare il dialog manualmente,
 * oppure il global handler registrato in api.ts via setMfaPromptHandler.
 *
 * Pattern: imperative API (call promptMfa(), dialog opens). Niente
 * Promise/return value — il dialog gestisce il flow internamente.
 */
export interface MfaPromptOptions {
  /**
   * Callback invoked dopo verify riuscito. Tipicamente la chiamata API
   * originale (refetch, retry mutation, ...).
   */
  onVerified?: () => void;
  /**
   * Callback invoked se il dialog viene chiuso/annullato SENZA verify (backlog
   * 8.4). Permette al chiamante (es. `apiRequest`) di rigettare la Promise in
   * sospeso invece di lasciarla appesa.
   */
  onCancelled?: () => void;
}

export interface MfaVerifyContextValue {
  /** Apre il dialog. Dialog gestisce TOTP verify + chiusura. */
  promptMfa: (opts?: MfaPromptOptions) => void;
  /**
   * Counter incrementato ogni volta che il dialog ha completato un verify
   * riuscito. I form (UserDrawer ecc.) usano `useResetMfaErrorOnVerify` per
   * resettare le proprie mutation `mfa_required` quando il counter cambia.
   */
  verifySuccessCount: number;
}

export const MfaVerifyContext = createContext<MfaVerifyContextValue | null>(null);

export function useMfaVerifyPrompt(): MfaVerifyContextValue {
  const ctx = useContext(MfaVerifyContext);
  if (!ctx) {
    throw new Error("useMfaVerifyPrompt must be used inside <MfaVerifyProvider>");
  }
  return ctx;
}

/**
 * Helper: ritorna true se l'errore e' un 403 mfa_required.
 * Utile per i callsite che vogliono distinguere "MFA needed" da altri errori.
 */
export function isMfaRequiredError(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.status === 403 &&
    error.problem.kind === "mfa_required"
  );
}

/**
 * Hook utility: resetta le mutation passate quando il MfaVerifyDialog ha
 * appena completato un verify riuscito (`verifySuccessCount` incrementa).
 *
 * Risolve il problema UX: dopo MFA verify ok, il box rosso "Verifica MFA
 * richiesta" rimaneva visibile nel form chiamante perche' la mutation
 * referenziata via `useMutation()` manteneva ancora `.error` (la rimozione
 * dalla cache non triggera re-render degli observer).
 *
 * Usage:
 *   useResetMfaErrorOnVerify(deleteMutation, resetPwdMutation, resetMfaMutation);
 */
export function useResetMfaErrorOnVerify(...mutations: ResettableMutation[]): void {
  const { verifySuccessCount } = useMfaVerifyPrompt();
  useEffect(() => {
    if (verifySuccessCount === 0) return;
    for (const m of mutations) {
      if (m.error && isMfaRequiredError(m.error)) {
        m.reset();
      }
    }
    // mutations e' un array di ref ma il reset triggera re-render solo se
    // gli error matchavano; dipendenza solo su counter per evitare loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verifySuccessCount]);
}
